import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from 'theme/elements/Page';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { hasPermission } from 'components/AuthorizationComponent';
import { WarningDialog } from 'components/WarningDialog';
import { permissionEnums } from 'constants/permission.constants';
import { pagesEnum } from 'constants/ui.constants';
import { generalStatusEnum } from 'constants/global.constants';
import {
	getSystemMessagesError,
	setError,
} from 'state/reducers/system-messages.reducer';
import { getToken } from 'state/reducers/auth.reducers';
import { getCompanyDashboard } from 'state/reducers/customer-accounts.reducers';
import { getUserDetails } from 'state/reducers/user.reducers';
import { createSystemMessage, createSystemMessageForCompany } from 'services/system-messages.service';
import { getCompanyNameByLanguage } from 'utils/index';
import { CreateForm } from 'sections/SystemMessages/CreateForm';
import type {
	ICreateSystemMessagePayload,
	ISystemMessageCompanyContact,
	SystemMessageFormMode,
} from 'types/system-messages.types';

type CreateSystemMessagePageProps = {
	mode: SystemMessageFormMode;
};

export const CreateSystemMessagePage = ({ mode }: CreateSystemMessagePageProps) => {
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const error = useAppSelector(getSystemMessagesError);
	const companyData = useAppSelector(getCompanyDashboard);
	const companyId = companyData?.basicData?.id;
	const token = useAppSelector(getToken);
	const { permissions } = useAppSelector(getUserDetails);
	const permissionList = Array.isArray(permissions) ? permissions : permissions?.permissions;

	const canCreate = hasPermission([
		mode === 'broadcast' ? permissionEnums.CREATE_SYSTEM_MESSAGE : permissionEnums.CREATE_SYSTEM_MESSAGE_COMPANY,
	]);
	const companyContacts = useMemo(() => {
		if (mode !== 'company') {
			return [];
		}
		return ((companyData?.contacts ?? []) as ISystemMessageCompanyContact[]).filter(
			(contact) => contact.status === generalStatusEnum.APPROVED
		);
	}, [mode, companyData?.contacts]);

	useEffect(() => {
		if (token && (!permissionList || permissionList.length === 0)) {
			return;
		}
		if (!canCreate) {
			navigate(`/${pagesEnum.PAGE_NOT_FOUND}`);
		}
	}, [token, permissionList, canCreate, navigate]);

	useEffect(() => {
		if (mode === 'company' && !companyId) {
			navigate(`/${pagesEnum.CUSTOMER_ACCOUNTS}`);
		}
	}, [mode, companyId, navigate]);

	const goBackUrl =
		mode === 'broadcast' ? `/${pagesEnum.SYSTEM_MESSAGES}` : `/${pagesEnum.CUSTOMER_ACCOUNTS}/${companyId}`;

	const handleSubmit = async (payload: ICreateSystemMessagePayload) => {
		if (mode === 'company' && !companyId) {
			navigate(`/${pagesEnum.CUSTOMER_ACCOUNTS}`);
			return;
		}

		const res =
			mode === 'broadcast'
				? await dispatch(createSystemMessage(payload))
				: await dispatch(createSystemMessageForCompany(payload, companyId!));
		if (res?.success) {
			navigate(goBackUrl);
		}
	};

	const pageTitle =
		mode === 'broadcast'
			? t('page.systemMessages.create.title.broadcast')
			: `${t('page.systemMessages.create.title.company')} - ${getCompanyNameByLanguage(companyData?.basicData)}`;

	const formCardTitle =
		mode === 'broadcast'
			? t('page.systemMessages.createCard.title')
			: t('page.systemMessages.createCard.title.company');

	if (!canCreate) {
		return null;
	}

	if (mode === 'company' && !companyId) {
		return null;
	}

	return (
		<Page title={pageTitle} includeGoBack goBackUrl={goBackUrl}>
			<WarningDialog
				open={error.exist}
				subTitle={error.text ? t(error.text) : ''}
				handleCancel={() => dispatch(setError({ exist: false }))}
			/>
			<CreateForm
				mode={mode}
				cardTitle={formCardTitle}
				companyId={companyId}
				companyContacts={companyContacts}
				onSubmit={handleSubmit}
			/>
		</Page>
	);
};
